import { createSignal, onMount, For, Show, createMemo } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { firstError } from '../api/envelope';

interface ProxyRow {
    id: string;
    poolId: string;
    poolTitle: string;
    isResidentialPool: boolean;
    externalId: string;
    host: string;
    port: number;
    countryCode: string;
    valid: boolean;
    isActive: boolean;
    lastSyncedAt: string;
    totalAttempts: number;
    totalSuccess: number;
    consecutiveFailures: number;
    lastFailureAt: string | null;
    lastFailureReason: string | null;
    successRate: number | null;
}

export default function AdminVotingProxies() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [rows, setRows] = createStore<ProxyRow[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [syncing, setSyncing] = createSignal(false);
    const [filterCountry, setFilterCountry] = createSignal<string>('');
    const [filterFailedOnly, setFilterFailedOnly] = createSignal(false);
    const [filterPool, setFilterPool] = createSignal<string>('');

    const poolTitles = createMemo(() => [...new Set(rows.map(r => r.poolTitle))].sort());

    const fetchAll = async () => {
        const res = await api.voting.adminProxies();
        if (isOk(res.data)) {
            const v = res.data.value as unknown as { proxies?: ProxyRow[] } | ProxyRow[] | undefined;
            setRows(reconcile((Array.isArray(v) ? v : (v?.proxies ?? [])) as ProxyRow[], { key: 'id' }));
        }
        if (loading()) setLoading(false);
    };

    onMount(() => {
        void fetchAll();
        useVisibilityPoll(() => void fetchAll(), 15_000);
    });

    const triggerSync = async () => {
        setSyncing(true);
        const res = await api.voting.adminProxySync();
        setSyncing(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_PROXIES_SYNC_FAILED')); return; }
        const r = res.data.value as unknown as { total: number; inserted: number; updated: number; reactivated: number; deactivated: number };
        toast?.info(t('ADMIN_VOTING_PROXIES_SYNC_RESULT', r.total, r.inserted, r.updated, r.deactivated));
        void fetchAll();
    };

    const replace = async (row: ProxyRow) => {
        const ok = await showConfirm(t('ADMIN_VOTING_PROXIES_REPLACE_CONFIRM', `${row.host}:${row.port}`, row.countryCode), true);
        if (!ok) return;
        const res = await api.voting.adminProxyReplace(row.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_PROXIES_REPLACE_FAILED')); return; }
        toast?.info(t('ADMIN_VOTING_PROXIES_REPLACED'));
        void fetchAll();
    };

    const countries = createMemo(() => {
        const set = new Set<string>();
        for (const r of rows) if (r.countryCode) set.add(r.countryCode);
        return Array.from(set).sort();
    });

    const filtered = createMemo(() => {
        let list = rows;
        if (filterCountry()) list = list.filter(r => r.countryCode === filterCountry());
        if (filterPool()) list = list.filter(r => r.poolTitle === filterPool());
        if (filterFailedOnly()) list = list.filter(r => r.consecutiveFailures > 0 || !r.valid || !r.isActive);
        return list;
    });

    const poolCounts = createMemo(() => {
        let dc = 0, res = 0;
        for (const r of rows) {
            if (r.isResidentialPool) res++; else dc++;
        }
        return { dc, res };
    });

    return (
        <div class="dashboard-page" style={{ 'max-width': '1400px', margin: '0 auto', padding: 'var(--spacing-lg)' }}>
            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                <h1 style={{ margin: 0 }}><Text val="ADMIN_VOTING_PROXIES_TITLE" /></h1>
                <button class="btn btn-primary" disabled={syncing()} onClick={triggerSync}>
                    <Show when={syncing()} fallback={<><i class="fa-solid fa-rotate" /> <Text val="ADMIN_VOTING_PROXIES_SYNC" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="ADMIN_VOTING_PROXIES_SYNCING" />
                    </Show>
                </button>
            </div>

            <p style={{ color: 'var(--dark-text-muted)', margin: 'var(--spacing-sm) 0 var(--spacing-lg)' }}>
                <Text val="ADMIN_VOTING_PROXIES_HELP" />
            </p>

            <div class="h" style={{ gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-md)', 'flex-wrap': 'wrap', 'align-items': 'center' }}>
                <label class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                    <span><Text val="ADMIN_VOTING_PROXIES_COUNTRY" /></span>
                    <select value={filterCountry()} onChange={e => setFilterCountry(e.currentTarget.value)}>
                        <option value="">--</option>
                        <For each={countries()}>{(c) => <option value={c}>{c}</option>}</For>
                    </select>
                </label>
                <label class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                    <span><Text val="ADMIN_VOTING_PROXIES_POOL" /></span>
                    <select value={filterPool()} onChange={e => setFilterPool(e.currentTarget.value)}>
                        <option value="">--</option>
                        <For each={poolTitles()}>{(title) => <option value={title}>{title}</option>}</For>
                    </select>
                </label>
                <label class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                    <input type="checkbox" checked={filterFailedOnly()} onChange={e => setFilterFailedOnly(e.currentTarget.checked)} />
                    <span><Text val="ADMIN_VOTING_PROXIES_PROBLEMS_ONLY" /></span>
                </label>
                <span style={{ 'margin-left': 'auto', color: 'var(--dark-text-muted)' }}>
                    {filtered().length} / {rows.length} <span style={{ 'margin-left': 'var(--spacing-md)' }}>{t('ADMIN_VOTING_PROXIES_POOL_DC')}: {poolCounts().dc} · {t('ADMIN_VOTING_PROXIES_POOL_RESIDENTIAL')}: {poolCounts().res}</span>
                </span>
            </div>

            <Show when={!loading()} fallback={<div style={{ color: 'var(--dark-text-muted)' }}>{t('LOADING')}</div>}>
                <Show when={filtered().length > 0} fallback={
                    <EmptyState icon="fa-network-wired" title={t('ADMIN_VOTING_PROXIES_EMPTY')} />
                }>
                    <div class="fcard" style={{ padding: 0, 'overflow-x': 'auto' }}>
                        <table style={{ width: '100%', 'border-collapse': 'collapse', 'font-size': 'var(--font-size-sm)' }}>
                            <thead>
                                <tr style={{ 'background': 'var(--dark-bg-tertiary)' }}>
                                    <th style={{ 'text-align': 'left', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_POOL" /></th>
                                    <th style={{ 'text-align': 'left', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_COUNTRY" /></th>
                                    <th style={{ 'text-align': 'left', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_HOST" /></th>
                                    <th style={{ 'text-align': 'left', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_STATUS" /></th>
                                    <th style={{ 'text-align': 'right', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_ATTEMPTS" /></th>
                                    <th style={{ 'text-align': 'right', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_SUCCESS_RATE" /></th>
                                    <th style={{ 'text-align': 'right', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_FAIL_STREAK" /></th>
                                    <th style={{ 'text-align': 'left', padding: 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_PROXIES_LAST_FAILURE" /></th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                <For each={filtered()}>{(r) => (
                                    <tr style={{ 'border-top': '1px solid var(--dark-border)' }}>
                                        <td style={{ padding: 'var(--spacing-sm)' }}>
                                            <span class="minicard" style={{
                                                'font-size': 'var(--font-size-xs)',
                                                'background': r.isResidentialPool ? 'var(--accent-purple-bg)' : 'var(--dark-bg-tertiary)',
                                                'color': r.isResidentialPool ? 'var(--accent-purple)' : 'var(--dark-text-secondary)',
                                            }}>{r.poolTitle}</span>
                                        </td>
                                        <td style={{ padding: 'var(--spacing-sm)', 'font-family': 'var(--font-family-mono)' }}>{r.countryCode || '-'}</td>
                                        <td style={{ padding: 'var(--spacing-sm)', 'font-family': 'var(--font-family-mono)' }}>{r.host}:{r.port}</td>
                                        <td style={{ padding: 'var(--spacing-sm)' }}>
                                            <Show when={!r.isActive}>
                                                <span style={{ color: 'var(--error-500)' }}><Text val="ADMIN_VOTING_PROXIES_INACTIVE" /></span>
                                            </Show>
                                            <Show when={r.isActive && !r.valid}>
                                                <span style={{ color: 'var(--warning-500)' }}><Text val="ADMIN_VOTING_PROXIES_INVALID" /></span>
                                            </Show>
                                            <Show when={r.isActive && r.valid}>
                                                <span style={{ color: 'var(--success-500)' }}><Text val="ADMIN_VOTING_PROXIES_OK" /></span>
                                            </Show>
                                        </td>
                                        <td style={{ padding: 'var(--spacing-sm)', 'text-align': 'right', 'font-family': 'var(--font-family-mono)' }}>{r.totalSuccess}/{r.totalAttempts}</td>
                                        <td style={{ padding: 'var(--spacing-sm)', 'text-align': 'right', 'font-family': 'var(--font-family-mono)' }}>{r.successRate == null ? '-' : `${r.successRate}%`}</td>
                                        <td style={{ padding: 'var(--spacing-sm)', 'text-align': 'right', color: r.consecutiveFailures > 0 ? 'var(--error-500)' : undefined }}>{r.consecutiveFailures}</td>
                                        <td style={{ padding: 'var(--spacing-sm)', 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'max-width': '20rem', 'overflow': 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' }} title={r.lastFailureReason ?? ''}>
                                            <Show when={r.lastFailureAt} fallback="-">
                                                {formatDateTime(r.lastFailureAt!)} {r.lastFailureReason ? `· ${r.lastFailureReason}` : ''}
                                            </Show>
                                        </td>
                                        <td style={{ padding: 'var(--spacing-sm)' }}>
                                            <button class="btn btn-secondary btn-sm" onClick={() => replace(r)} title={t('ADMIN_VOTING_PROXIES_REPLACE_TITLE')}>
                                                <i class="fa-solid fa-rotate-right" />
                                            </button>
                                        </td>
                                    </tr>
                                )}</For>
                            </tbody>
                        </table>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
